import FilterForm from '../../../components/forms/FilterForm';
import {
  CanAccess,
  CrudFilters,
  HttpError,
  useNavigation
} from '@refinedev/core';
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  EyeOutlined
} from '@ant-design/icons';
import {
  EditButton,
  TagField,
  TextField,
  useSelect,
  useTable
} from '@refinedev/antd';
import { Form, Input, Select, Space, Table } from 'antd';
import { ICourse } from 'interfaces/courses';
import { IExternalItem } from 'interfaces/external_items';
import { IGraduateCourse } from 'interfaces/graduate_courses';
import { IOffer } from '../../../interfaces/offers';
import { IProduct } from 'interfaces/products';
import { IProductBundle } from 'interfaces/product_bundles';
import { IProductCategory } from 'interfaces/product_categories';
import { IProductSet } from 'interfaces/product_sets';
import { ListLayout } from '../../../components/layout/layout/ListLayout';
import { PRODUCTABLE_TYPES } from 'constants/admin/products';
import { useState } from 'react';

interface IFilter {
  name: string;
  productable_type: string;
  productable_id: string;
}

export const ProductList = () => {
  const { edit } = useNavigation();
  const [productableType, setProductableType] = useState<string>('Course');

  const { selectProps: courseSelectProps } = useSelect<ICourse>({
    resource: 'courses',
    optionLabel: 'name',
    optionValue: 'id',

    onSearch: value => [
      {
        field: 'filter[name]',
        operator: 'eq',
        value
      }
    ],

    pagination: {
      mode: 'server'
    }
  });

  const { selectProps: graduateCourseSelectProps } = useSelect<IGraduateCourse>(
    {
      resource: 'graduate_courses',
      optionLabel: 'name',
      optionValue: 'id',

      onSearch: value => [
        {
          field: 'filter[name]',
          operator: 'eq',
          value
        }
      ],

      pagination: {
        mode: 'server'
      }
    }
  );

  const { selectProps: setSelectProps } = useSelect<IProductSet>({
    resource: 'products/sets',
    optionLabel: 'name',
    optionValue: 'id',

    onSearch: value => [
      {
        field: 'filter[name]',
        operator: 'eq',
        value
      }
    ],

    pagination: {
      mode: 'server'
    }
  });

  const { selectProps: bundleSelectProps } = useSelect<IProductBundle>({
    resource: 'products/bundles',
    optionLabel: 'name',
    optionValue: 'id',

    onSearch: value => [
      {
        field: 'filter[name]',
        operator: 'eq',
        value
      }
    ],

    pagination: {
      mode: 'server'
    }
  });

  const { selectProps: externalItemSelectProps } = useSelect<IExternalItem>({
    resource: 'external_items',
    optionLabel: 'name',
    optionValue: 'id',

    onSearch: value => [
      {
        field: 'filter[name]',
        operator: 'eq',
        value
      }
    ],

    pagination: {
      mode: 'server'
    }
  });

  const { tableProps, searchFormProps } = useTable<
    IProduct,
    HttpError,
    IFilter
  >({
    onSearch: params => {
      const filters: CrudFilters = [];
      const { name, productable_type, productable_id } = params;

      filters.push(
        {
          field: 'filter[name]',
          operator: 'eq',
          value: name
        },
        {
          field: 'filter[productable_type]',
          operator: 'eq',
          value: productable_type
        },
        {
          field: 'filter[productable_id]',
          operator: 'eq',
          value: productable_id
        }
      );

      return filters;
    }
  });

  return (
    <ListLayout title="Produtos">
      <FilterForm searchFormProps={searchFormProps}>
        <Form.Item name="name">
          <Input placeholder="Nome do produto" className="lg:w-96" />
        </Form.Item>

        <Form.Item
          name="productable_type"
          initialValue={PRODUCTABLE_TYPES[0].value}
        >
          <Select
            placeholder="Tipo de produto"
            onChange={value => setProductableType(value)}
          >
            {PRODUCTABLE_TYPES.map(type => (
              <Select.Option key={type.value}>{type.label}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="productable_id">
          {(() => {
            switch (productableType) {
              case 'Course': {
                return <Select {...courseSelectProps} placeholder="Curso" />;
              }

              case 'Graduate::Course': {
                return (
                  <Select
                    {...graduateCourseSelectProps}
                    placeholder="Curso de Pós-graduação"
                  />
                );
              }

              case 'Products::Set': {
                return <Select {...setSelectProps} placeholder="Set" />;
              }

              case 'Products::Bundle': {
                return <Select {...bundleSelectProps} placeholder="Combo" />;
              }

              case 'ExternalItem': {
                return (
                  <Select
                    {...externalItemSelectProps}
                    placeholder="Item externo"
                  />
                );
              }

              default: {
                return <Select {...courseSelectProps} placeholder="Curso" />;
              }
            }
          })()}
        </Form.Item>
      </FilterForm>

      <Table {...tableProps} rowKey="id">
        <Table.Column
          dataIndex="productable_type"
          key="type"
          title="Tipo"
          render={value => (
            <TextField
              value={
                PRODUCTABLE_TYPES.find(type => type.value === value)?.label
              }
            />
          )}
        />
        <Table.Column
          dataIndex="productable_name"
          key="productable_name"
          title="Associado a"
          render={value => <TextField value={value || '-'} />}
        />
        <Table.Column
          dataIndex="slug"
          key="slug"
          title="Slug"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="name"
          key="name"
          title="Nome"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="categories"
          key="categories"
          title="Categorias"
          render={categories =>
            categories.map((category: IProductCategory) => (
              <TagField
                style={{ border: 'none', margin: 4, cursor: 'pointer' }}
                color="purple"
                key={category.id}
                value={category.name}
                onClick={() =>
                  edit('ecommerce/products/categories', category.id)
                }
              />
            ))
          }
        />
        <Table.Column
          dataIndex="offers"
          key="offers"
          title="Possui oferta ativa?"
          align="center"
          render={offers =>
            offers.some((offer: IOffer) =>
              ['active', 'hidden'].includes(offer.status)
            ) ? (
              <CheckCircleTwoTone twoToneColor="#52c41a" />
            ) : (
              <CloseCircleTwoTone twoToneColor="#D92D20" />
            )
          }
        />
        <Table.Column<IProduct>
          title="Ações"
          dataIndex="actions"
          align="center"
          width={80}
          render={(_, record) => {
            return (
              <Space>
                <CanAccess resource="products" action="edit">
                  <EditButton
                    hideText
                    size="small"
                    icon={<EyeOutlined />}
                    recordItemId={record.id}
                  />
                </CanAccess>
              </Space>
            );
          }}
        />
      </Table>
    </ListLayout>
  );
};
